// An agent's long-term memory of the owner (SGL-417 Phase B; the memory page is SGL-115).
//
// chat-service /agent-memory. Each entry is one preference, correction, decision or
// open item; the agent saves them itself during chat, and the owner corrects them
// here. Every write sends the etag the page loaded, so a stale page gets 409 and
// reloads instead of overwriting a change made elsewhere (the agent, another tab).
import { CHAT_API_BASE_URL } from "./chatService";
import { authedFetch } from "../auth/authedFetch";

export type MemoryEntryType = "preference" | "correction" | "decision" | "open_item";
export type MemoryEntrySource = "agent" | "owner" | "approval_screen" | "background_merge" | "legacy";

export interface MemoryEntry {
  id: string;
  type: MemoryEntryType;
  text: string;
  reason: string;
  date: string;
  source: MemoryEntrySource;
  due: string;
  expired: boolean;
}

export interface AgentMemory {
  entries: MemoryEntry[];
  /** The whole memory as the owner edits it: headings and one "- text" line per entry. */
  text: string;
  etag: string | null;
}

/** Thrown on 409: memory changed since the page loaded. Reload, then retry. */
export class MemoryConflictError extends Error {}

function base(): string {
  if (!CHAT_API_BASE_URL) {
    throw new Error("Chat API is not configured (REACT_APP_CHAT_API_BASE_URL)");
  }
  return `${CHAT_API_BASE_URL.replace(/\/$/, "")}/agent-memory`;
}

async function read(resp: Response, what: string): Promise<AgentMemory> {
  if (resp.status === 409) {
    throw new MemoryConflictError("Memory changed since you opened it.");
  }
  if (!resp.ok) {
    const detail = await resp
      .json()
      .then((body) => (typeof body?.detail === "string" ? body.detail : null))
      .catch(() => null);
    throw new Error(detail || `Couldn't ${what} (HTTP ${resp.status}).`);
  }
  const data = await resp.json();
  return { entries: data.entries || [], text: data.text || "", etag: data.etag ?? null };
}

const path = (customerId: string, agentId: string) =>
  `${base()}/${encodeURIComponent(customerId)}/${encodeURIComponent(agentId)}`;

export async function fetchAgentMemory(customerId: string, agentId: string): Promise<AgentMemory> {
  return read(await authedFetch(path(customerId, agentId)), "load memory");
}

export async function updateMemoryEntry(
  customerId: string,
  agentId: string,
  entryId: string,
  changes: { text?: string; type?: MemoryEntryType; due?: string },
  etag: string | null
): Promise<AgentMemory> {
  const resp = await authedFetch(`${path(customerId, agentId)}/entries/${encodeURIComponent(entryId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...changes, etag }),
  });
  return read(resp, "save the change");
}

/** The Edit box saves the whole memory at once (SGL-576). Unchanged lines keep their
 *  history; a stale etag is a 409, so an entry the agent just saved is never lost. */
export async function saveMemoryText(
  customerId: string,
  agentId: string,
  text: string,
  etag: string | null
): Promise<AgentMemory> {
  const resp = await authedFetch(path(customerId, agentId), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, etag }),
  });
  return read(resp, "save memory");
}

export async function forgetMemoryEntry(
  customerId: string,
  agentId: string,
  entryId: string,
  etag: string | null,
  action: "forget" | "done" = "forget"
): Promise<AgentMemory> {
  const qs = new URLSearchParams({ action });
  if (etag) qs.set("etag", etag);
  const resp = await authedFetch(
    `${path(customerId, agentId)}/entries/${encodeURIComponent(entryId)}?${qs.toString()}`,
    { method: "DELETE" }
  );
  return read(resp, "remove the entry");
}

/** Approval screen → memory. Fire-and-forget by design: it runs after the post's own
 *  save succeeded and must never block or undo it. Failures are logged, not shown. */
export function sendApprovalSignal(
  customerId: string,
  signal: {
    post_id: string;
    action: "edit" | "reject";
    field?: string;
    before?: string;
    after?: string;
    reason?: string;
  }
): void {
  try {
    authedFetch(`${base()}/${encodeURIComponent(customerId)}/signals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(signal),
    })
      .then((resp) => {
        if (!resp.ok) console.warn(`Approval signal for post ${signal.post_id} failed (HTTP ${resp.status})`);
      })
      .catch((e) => console.warn(`Approval signal for post ${signal.post_id} failed`, e));
  } catch (e) {
    console.warn(`Approval signal for post ${signal.post_id} not sent`, e);
  }
}
